import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
import { insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, insuranceClaimsMoneyCentsSchema, insuranceClaimsPageSchema, } from './insurance-claims-claim.contract.js';
import { insuranceClaimsScopeCoveragePositionSchema } from './insurance-claims-scope.contract.js';
export const insuranceClaimsCarrierEstimateReviewsCapability = 'insurance-claims.carrier-estimate-review.v1';
export const insuranceClaimsCarrierEstimateReviewVersionsCapability = 'insurance-claims.carrier-estimate-review-versions.v1';
export const insuranceClaimsCurrentEstimateProviderSlot = 'insurance-claims.current-estimate-facts';
export const insuranceClaimsCurrentEstimateProviderId = 'estimating.server.insurance-claims-current-estimate';
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
export const insuranceClaimsCarrierEstimateSourceTypeSchema = z.enum([
    'carrier_csv', 'xactimate_csv', 'xactimate_pdf', 'scope_pdf', 'manual', 'other',
]);
export const insuranceClaimsCarrierEstimateImportSourceTypeSchema = z.enum([
    'carrier_csv', 'xactimate_csv',
]);
export const insuranceClaimsCarrierEstimateReviewStateSchema = z.enum([
    'imported', 'reviewing', 'prepared',
]);
export const insuranceClaimsCarrierEstimateLineOriginSchema = z.enum([
    'carrier', 'current-estimate',
]);
export const insuranceClaimsCarrierEstimateSuggestionSchema = z.enum([
    'uncompared', 'aligned', 'quantity-review', 'carrier-only', 'current-only',
]);
export const insuranceClaimsCarrierEstimateDecisionActionSchema = z.enum([
    'pending', 'no-action', 'include-candidate', 'exclude-candidate', 'needs-documentation',
]);
export const insuranceClaimsCarrierEstimateQuantitySchema = z.number().int().min(0).max(10_000_000_000);
export const insuranceClaimsCurrentEstimateProviderRequestSchema = z.strictObject({
    consumerModuleId: z.literal('insurance-claims'),
    requestedCapability: z.literal('estimating.estimates.v1'),
    resourceType: z.literal('insurance-claims.claim'),
    resourceId: insuranceClaimsIdSchema,
    preferredSourceId: z.string().uuid().nullable(),
    projectId: z.string().uuid(),
});
export const insuranceClaimsCarrierEstimateCurrentLineSchema = z.strictObject({
    lineKey: z.string().uuid(),
    lineNumber: z.number().int().positive(),
    sourceId: nullableText(240),
    sourceLabel: nullableText(240),
    groupLabel: nullableText(120),
    name: z.string().trim().min(1).max(240),
    description: nullableText(4_000),
    quantityTenThousandths: z.number().int().min(1).max(10_000_000),
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: insuranceClaimsMoneyCentsSchema,
    lineTotalCents: insuranceClaimsMoneyCentsSchema,
});
export const insuranceClaimsCarrierEstimateProviderFactSchema = z.strictObject({
    state: z.literal('present'),
    capability: z.literal('estimating.estimates.v1'),
    estimateId: z.string().uuid(),
    estimateVersionId: z.string().uuid(),
    projectId: z.string().uuid(),
    status: z.enum(['draft', 'released', 'viewed', 'accepted', 'declined', 'expired', 'voided']),
    revision: z.number().int().positive(),
    contentHash: sha256,
    title: z.string().trim().min(1).max(240),
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    totalCents: insuranceClaimsMoneyCentsSchema,
    lines: z.array(insuranceClaimsCarrierEstimateCurrentLineSchema).min(1).max(500),
});
export const insuranceClaimsCarrierEstimateProviderAvailabilitySchema = z.discriminatedUnion('state', [
    insuranceClaimsCarrierEstimateProviderFactSchema,
    z.strictObject({
        state: z.literal('unavailable'),
        capability: z.literal('estimating.estimates.v1'),
        reasonCode: z.enum([
            'not-installed', 'not-enabled', 'permission-denied', 'no-current-estimate',
            'preferred-estimate-not-found', 'provider-failed', 'provider-invalid',
        ]),
    }),
]);
export const insuranceClaimsCarrierEstimateSourceSnapshotSchema = z.strictObject({
    sourceType: insuranceClaimsCarrierEstimateSourceTypeSchema,
    carrierName: nullableText(200),
    claimNumber: nullableText(120),
    estimateNumber: nullableText(120),
    estimateDate: z.string().date().nullable(),
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    sourceContentHash: sha256,
    sourceFileName: nullableText(240),
    importedLineCount: z.number().int().min(1).max(500),
    totals: z.strictObject({
        rcvCents: insuranceClaimsMoneyCentsSchema.nullable(),
        acvCents: insuranceClaimsMoneyCentsSchema.nullable(),
        depreciationCents: insuranceClaimsMoneyCentsSchema.nullable(),
        deductibleCents: insuranceClaimsMoneyCentsSchema.nullable(),
    }),
});
export const insuranceClaimsCarrierEstimateLineDecisionSchema = z.strictObject({
    action: insuranceClaimsCarrierEstimateDecisionActionSchema,
    coveragePosition: insuranceClaimsScopeCoveragePositionSchema,
    note: nullableText(2_000),
});
export const insuranceClaimsCarrierEstimateReviewLineSchema = z.strictObject({
    lineKey: z.string().uuid(),
    origin: insuranceClaimsCarrierEstimateLineOriginSchema,
    sourceLineNumber: z.number().int().positive().nullable(),
    itemCode: nullableText(120),
    trade: nullableText(120),
    category: nullableText(120),
    area: nullableText(200),
    description: z.string().trim().min(1).max(4_000),
    carrierQuantityTenThousandths: insuranceClaimsCarrierEstimateQuantitySchema.nullable(),
    carrierUnit: nullableText(60),
    carrierUnitPriceCents: insuranceClaimsMoneyCentsSchema.nullable(),
    carrierRcvCents: insuranceClaimsMoneyCentsSchema.nullable(),
    carrierAcvCents: insuranceClaimsMoneyCentsSchema.nullable(),
    carrierDepreciationCents: insuranceClaimsMoneyCentsSchema.nullable(),
    currentEstimateLine: insuranceClaimsCarrierEstimateCurrentLineSchema.nullable(),
    matchScoreBasisPoints: z.number().int().min(0).max(10_000).nullable(),
    suggestion: insuranceClaimsCarrierEstimateSuggestionSchema,
    suggestedCandidateAmountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    decision: insuranceClaimsCarrierEstimateLineDecisionSchema,
}).superRefine((value, context) => {
    if (value.origin === 'carrier' && value.sourceLineNumber === null) {
        context.addIssue({ code: 'custom', path: ['sourceLineNumber'], message: 'Carrier rows retain a source line number.' });
    }
    if (value.origin === 'current-estimate' && value.currentEstimateLine === null) {
        context.addIssue({ code: 'custom', path: ['currentEstimateLine'], message: 'Current-only rows retain their Estimating line snapshot.' });
    }
    if (value.decision.action === 'include-candidate' && value.suggestedCandidateAmountCents === null) {
        context.addIssue({ code: 'custom', path: ['decision', 'action'], message: 'Only a quantified candidate can be included.' });
    }
    if (value.decision.action !== 'pending' && (value.decision.note === null || value.decision.note.length < 10)) {
        context.addIssue({ code: 'custom', path: ['decision', 'note'], message: 'A retained decision explanation is required.' });
    }
});
export const insuranceClaimsCarrierEstimateComparisonSummarySchema = z.strictObject({
    carrierLineCount: z.number().int().nonnegative(),
    currentEstimateLineCount: z.number().int().nonnegative(),
    matchedLineCount: z.number().int().nonnegative(),
    alignedLineCount: z.number().int().nonnegative(),
    quantityReviewCount: z.number().int().nonnegative(),
    carrierOnlyCount: z.number().int().nonnegative(),
    currentOnlyCount: z.number().int().nonnegative(),
    pendingDecisionCount: z.number().int().nonnegative(),
    includedCandidateCount: z.number().int().nonnegative(),
    includedCandidateAmountCents: insuranceClaimsMoneyCentsSchema,
});
export const insuranceClaimsCarrierEstimatePreparationSchema = z.strictObject({
    title: z.string().trim().min(1).max(300),
    preparedAt: z.string().datetime({ offset: true }),
    sourceReviewVersionId: z.string().uuid(),
    sourceEstimateVersionId: z.string().uuid(),
    sourceEstimateContentHash: sha256,
    candidateLineKeys: z.array(z.string().uuid()).min(1).max(500),
    requestedAmountCents: insuranceClaimsMoneyCentsSchema,
});
export const insuranceClaimsCarrierEstimateReviewPublicDataSchema = z.strictObject({
    claimId: insuranceClaimsIdSchema,
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    source: insuranceClaimsCarrierEstimateSourceSnapshotSchema,
    currentEstimate: z.discriminatedUnion('state', [
        z.strictObject({ state: z.literal('not-requested') }),
        z.strictObject({
            state: z.literal('unavailable'),
            capability: z.literal('estimating.estimates.v1'),
            reasonCode: insuranceClaimsCarrierEstimateProviderAvailabilitySchema.options[1].shape.reasonCode,
        }),
        insuranceClaimsCarrierEstimateProviderFactSchema,
    ]),
    lines: z.array(insuranceClaimsCarrierEstimateReviewLineSchema).min(1).max(1_000),
    summary: insuranceClaimsCarrierEstimateComparisonSummarySchema,
    preparation: insuranceClaimsCarrierEstimatePreparationSchema.nullable(),
});
export const insuranceClaimsCarrierEstimateReviewVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsCarrierEstimateReviewVersionsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    reviewId: z.string().uuid(),
    claimId: insuranceClaimsIdSchema,
    projectId: z.string().uuid(),
    revision: z.number().int().positive(),
    state: insuranceClaimsCarrierEstimateReviewStateSchema,
    commandKind: z.enum(['create', 'compare', 'decide-line', 'prepare']),
    contentHash: sha256,
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsCarrierEstimateReviewPublicDataSchema,
});
export const insuranceClaimsCarrierEstimateReviewSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsCarrierEstimateReviewsCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    claimId: insuranceClaimsIdSchema,
    projectId: z.string().uuid(),
    state: insuranceClaimsCarrierEstimateReviewStateSchema,
    revision: z.number().int().positive(),
    currentVersionId: z.string().uuid(),
    preparedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsCarrierEstimateReviewVersionSchema,
});
export const insuranceClaimsCreateCarrierEstimateReviewCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    reviewId: z.string().uuid(),
    claimId: insuranceClaimsIdSchema,
    source: z.strictObject({
        sourceType: insuranceClaimsCarrierEstimateImportSourceTypeSchema,
        carrierName: nullableText(200).default(null),
        claimNumber: nullableText(120).default(null),
        estimateNumber: nullableText(120).default(null),
        estimateDate: z.string().date().nullable().default(null),
        currencyCode: z.string().regex(/^[A-Z]{3}$/),
        sourceFileName: nullableText(240).default(null),
        deductibleCents: insuranceClaimsMoneyCentsSchema.nullable().default(null),
        csvText: z.string().min(1).max(750_000),
    }),
    reason: z.string().trim().min(10).max(500),
});
const commandBase = {
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
};
export const insuranceClaimsCompareCarrierEstimateCommandSchema = z.strictObject({
    kind: z.literal('compare'),
    ...commandBase,
    preferredEstimateId: z.string().uuid().nullable().default(null),
});
export const insuranceClaimsDecideCarrierEstimateLineCommandSchema = z.strictObject({
    kind: z.literal('decide-line'),
    ...commandBase,
    lineKey: z.string().uuid(),
    decision: insuranceClaimsCarrierEstimateLineDecisionSchema,
});
export const insuranceClaimsPrepareCarrierEstimateCandidatesCommandSchema = z.strictObject({
    kind: z.literal('prepare'),
    ...commandBase,
    title: z.string().trim().min(1).max(300),
});
export const insuranceClaimsCarrierEstimateReviewCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsCompareCarrierEstimateCommandSchema,
    insuranceClaimsDecideCarrierEstimateLineCommandSchema,
    insuranceClaimsPrepareCarrierEstimateCandidatesCommandSchema,
]);
export const insuranceClaimsCarrierEstimateReviewListQuerySchema = z.strictObject({
    claimId: insuranceClaimsIdSchema.nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
    state: insuranceClaimsCarrierEstimateReviewStateSchema.nullable().default(null),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const insuranceClaimsCarrierEstimateReviewResponseSchema = z.strictObject({
    data: insuranceClaimsCarrierEstimateReviewSchema,
    provider: insuranceClaimsCarrierEstimateProviderAvailabilitySchema.optional(),
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export const insuranceClaimsCarrierEstimateReviewsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsCarrierEstimateReviewSchema),
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsCarrierEstimateReviewVersionsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsCarrierEstimateReviewVersionSchema),
});
//# sourceMappingURL=insurance-claims-carrier-estimate-review.contract.js.map